import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BellOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Pagination } from "antd";
import {
  approveNoticeApi,
  deleteNoticeApi,
  getNoticeListApi,
  getUnreadNotifyCountApi,
  markNoticeAsReadApi,
} from "../../service/notice";
import { getLanguageName } from "../../utils/languageUtils";
import { useAlert } from "../../hooks/useAlert";
import "./NotificationCenter.scss";

const PAGE_SIZE = 6;

/** 与弹窗列表渲染一致：按页号从接口返回的 notices 中取当前展示行 */
function visibleRowsForNoticePage(
  response: NOTICE.NoticeListResponse | null,
  pageNo: number
): NOTICE.NoticeItem[] {
  const allNotices = response?.notices || [];
  const start = (pageNo - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pagedNotices = allNotices.slice(start, end);
  return pagedNotices.length > 0 ? pagedNotices : allNotices;
}

export type NotificationInboxVariant = "navbar" | "home";

export type UseNotificationInboxOptions = {
  showSuccess?: (msg: string) => void;
  showError?: (msg: string) => void;
  /** 无通知权限时（false）不拉取接口、不渲染入口（默认 true） */
  enabled?: boolean;
};

export function useNotificationInbox(options?: UseNotificationInboxOptions) {
  const fallback = useAlert();
  const showSuccess = options?.showSuccess ?? fallback.showSuccess;
  const showError = options?.showError ?? fallback.showError;
  const enabled = options?.enabled ?? true;
  const [inboxOpen, setInboxOpen] = useState(false);
  const [noticeList, setNoticeList] = useState<NOTICE.NoticeListResponse | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  /** 本次打开弹窗期间，在任意分页上展示过的「可标已读」通知 id（notice_type=2 且未读） */
  const displayedUnreadType2IdsRef = useRef<Set<number>>(new Set());
  /** 与弹窗开关同步更新，避免列表请求快于 React effect 时漏记「展示过」的 id */
  const inboxOpenRef = useRef(false);

  const fetchUnreadCount = useCallback(async (retry = 0) => {
    try {
      const res = await getUnreadNotifyCountApi();
      if (res && res.code === 0 && typeof res.data === "number") {
        setUnreadCount(res.data);
      } else if (retry < 2) {
        await fetchUnreadCount(retry + 1);
      }
    } catch (error) {
      console.error("获取未读通知数量失败:", error);
      if (retry < 2) {
        await fetchUnreadCount(retry + 1);
      }
    }
  }, []);

  const applyNoticeListAndTrackDisplayed = useCallback(
    (response: NOTICE.NoticeListResponse | null, pageNo: number) => {
      setNoticeList(response);
      if (!inboxOpenRef.current || !response?.notices?.length) return;
      for (const n of visibleRowsForNoticePage(response, pageNo)) {
        if (n.notice.notice_type === "2" && n.read_status === "0") {
          displayedUnreadType2IdsRef.current.add(n.id);
        }
      }
    },
    []
  );

  const fetchNoticeListForPage = useCallback(
    async (page: number) => {
      try {
        const response = await getNoticeListApi({
          unread_only: false,
          page,
          page_size: PAGE_SIZE,
        });
        applyNoticeListAndTrackDisplayed(response, page);
      } catch (error) {
        console.error("获取通知列表失败:", error);
        applyNoticeListAndTrackDisplayed(null, page);
      }
    },
    [applyNoticeListAndTrackDisplayed]
  );

  useEffect(() => {
    if (!enabled) return;
    void fetchNoticeListForPage(1);
    void fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅初始化拉取
  }, [fetchNoticeListForPage, fetchUnreadCount, enabled]);

  const openInbox = useCallback(() => {
    if (!enabled) return;
    displayedUnreadType2IdsRef.current.clear();
    setCurrentPage(1);
    inboxOpenRef.current = true;
    setInboxOpen(true);
    void fetchNoticeListForPage(1);
  }, [enabled, fetchNoticeListForPage]);

  const closeInbox = useCallback(() => {
    const ids = Array.from(displayedUnreadType2IdsRef.current);
    displayedUnreadType2IdsRef.current.clear();
    inboxOpenRef.current = false;
    setInboxOpen(false);
    setCurrentPage(1);
    void (async () => {
      if (ids.length === 0) return;
      await Promise.allSettled(ids.map((id) => markNoticeAsReadApi({ id })));
      await fetchUnreadCount();
      void fetchNoticeListForPage(1);
    })();
  }, [fetchNoticeListForPage, fetchUnreadCount]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openInbox();
    }
  };

  const handleDeleteNotification = async (id: number) => {
    try {
      await deleteNoticeApi(id);
      const response = await getNoticeListApi({
        unread_only: false,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      applyNoticeListAndTrackDisplayed(response, currentPage);
      await fetchUnreadCount();
      showSuccess("通知已删除");
    } catch (error) {
      console.error("删除通知失败:", error);
      showError("删除通知失败，请重试");
    }
  };

  const handleApproval = async (id: number, action: "approve" | "reject") => {
    try {
      const approve = action === "approve";
      const response = await approveNoticeApi({ id, approve });

      if (response.code === 0) {
        showSuccess(approve ? "已通过审核" : "已驳回");
      } else {
        const msg = response.msg || "处理失败，请重试";
        showError(msg);
        return;
      }

      const updatedResponse = await getNoticeListApi({
        unread_only: false,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      applyNoticeListAndTrackDisplayed(updatedResponse, currentPage);
      await fetchUnreadCount();
    } catch (error: unknown) {
      console.error("审核操作失败:", error);
      const err = error as {
        response?: { detail?: string; message?: string; info?: string };
        status?: number;
        message?: string;
      };
      let errorMessage = "处理失败，请重试";
      if (err?.response?.detail) {
        errorMessage = err.response.detail;
      } else if (err?.response?.message) {
        errorMessage = err.response.message;
      } else if (err?.response?.info) {
        errorMessage = err.response.info;
      } else if (err?.status) {
        if (err.status === 500) {
          errorMessage = "服务器内部错误，请稍后重试";
        } else if (err.status === 404) {
          errorMessage = "请求的资源不存在";
        } else if (err.status === 403) {
          errorMessage = "没有权限执行此操作";
        } else if (err.status === 401) {
          errorMessage = "登录验证失效，请重新登录";
        } else {
          errorMessage = `请求失败 (状态码: ${err.status})`;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      showError(errorMessage);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    try {
      const response = await getNoticeListApi({
        unread_only: false,
        page,
        page_size: PAGE_SIZE,
      });
      applyNoticeListAndTrackDisplayed(response, page);
    } catch (error) {
      console.error("获取通知列表失败:", error);
      showError("获取通知列表失败，请重试");
    }
  };

  const badge =
    unreadCount > 0 ? (
      <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
    ) : null;

  const inboxTitle: ReactNode = useMemo(
    () => (
      <div className="notification-modal-title">
        <span>信息提醒</span>
        <span className="notification-count">(共{noticeList?.total || 0}条通知)</span>
      </div>
    ),
    [noticeList?.total]
  );

  const renderTrigger = (variant: NotificationInboxVariant) => {
    if (!enabled) return null;
    return variant === "navbar" ? (
      <div
        className="notification-icon"
        onClick={openInbox}
        role="button"
        tabIndex={0}
        onKeyDown={handleTriggerKeyDown}
      >
        <i className="fas fa-envelope" />
        {badge}
      </div>
    ) : (
      <div
        className="notification-center--home"
        onClick={openInbox}
        role="button"
        tabIndex={0}
        onKeyDown={handleTriggerKeyDown}
      >
        <BellOutlined className="notification-center--home__icon" />
        {badge}
      </div>
    );
  };

  const renderInboxContent = () => {
    const allNotices = noticeList?.notices || [];
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    const pagedNotices = allNotices.slice(start, end);
    const list = pagedNotices.length > 0 ? pagedNotices : allNotices;

    const rows =
      list.length > 0 ? (
        list.map((notice) => {
            const shouldBeGrayed =
              notice.notice.notice_type === "2"
                ? notice.read_status === "1"
                : notice.notice.notice_type === "3"
                  ? notice.process_status !== "0" && notice.process_status !== "3"
                  : false;

            const showNewBadge =
              (notice.notice.notice_type === "2" && notice.read_status === "0") ||
              (notice.notice.notice_type === "3" && notice.process_status === "0");

            return (
              <div key={notice.id} className={`notification-item ${shouldBeGrayed ? "read" : ""}`}>
                <div className="notification-text">
                  <span className="bullet">•</span>
                  {(() => {
                    const rawContent = notice.notice.notice_content || "";
                    const targetLangCode =
                      notice.notice_object?.target_lang ||
                      notice.notice_object?.targetLang ||
                      notice.notice_object?.lang ||
                      "";

                    if (!targetLangCode) {
                      return <span className="content">{rawContent}</span>;
                    }

                    const langName = getLanguageName(targetLangCode);
                    const replaced = rawContent.replace(targetLangCode, langName);

                    return <span className="content">{replaced}</span>;
                  })()}
                </div>
                <div className="notification-actions">
                  <span className="notification-date">
                    {new Date(notice.create_time).toLocaleString("zh-CN", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false,
                    })}
                  </span>
                  {notice.notice.notice_type === "3" && notice.process_status === "0" && (
                    <div className="action-buttons">
                      <Button
                        size="small"
                        className="approve-btn"
                        onClick={() => handleApproval(notice.id, "approve")}
                      >
                        通过
                      </Button>
                      <Button
                        size="small"
                        className="reject-btn"
                        onClick={() => handleApproval(notice.id, "reject")}
                      >
                        不通过
                      </Button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="home-series__icon-btn home-series__icon-btn--danger"
                    title="删除"
                    aria-label="删除通知"
                    onClick={() => handleDeleteNotification(notice.id)}
                  >
                    <DeleteOutlined style={{ fontSize: 14 }} />
                  </button>
                </div>
                {showNewBadge && (
                  <span className="notification-new-badge" role="img" aria-label="新通知">
                    NEW
                  </span>
                )}
              </div>
            );
          })
      ) : (
        <div className="no-notifications">
          <p>暂无通知</p>
        </div>
      );

    return (
      <>
        <div className="notification-content">
          <div className="notification-content__scroll">{rows}</div>
        </div>

        <div className="notification-pagination">
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={noticeList?.total || noticeList?.notices?.length || 0}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
            simple
          />
        </div>
      </>
    );
  };

  return {
    inboxOpen,
    openInbox,
    closeInbox,
    inboxTitle,
    renderTrigger,
    renderInboxContent,
    unreadCount,
  };
}
